import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Storage as DatabaseIcon,
  CloudOff as MockDataIcon,
  Cloud as LiveDataIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const DataStatus: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Data Integration Status
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Current Situation: Mix of Mock Data and Live Database Integration
        </Typography>
        <Typography>
          The system currently has both mock data endpoints and live SIS database integration. 
          Here's how to access each type of data:
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        {/* Mock Data Pages */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MockDataIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Mock Data Pages
                </Typography>
                <Chip label="Demo Data" color="warning" size="small" sx={{ ml: 2 }} />
              </Box>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                These pages use simulated data for demonstration purposes:
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <MockDataIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Dashboard" 
                    secondary="Mock statistics and charts"
                  />
                  <Button 
                    size="small" 
                    onClick={() => navigate('/')}
                  >
                    Visit
                  </Button>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <MockDataIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Analytics" 
                    secondary="Simulated analytics data"
                  />
                  <Button 
                    size="small" 
                    onClick={() => navigate('/analytics')}
                  >
                    Visit
                  </Button>
                </ListItem>
              </List>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  <strong>API Issue:</strong> The URL you saw with encoded parameters 
                  (<code>%5B%5D</code>) indicates React Query parameters are being 
                  sent to the backend, which is not optimal.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Live Database Pages */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LiveDataIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Live SIS Database Pages
                </Typography>
                <Chip label="Real Data" color="success" size="small" sx={{ ml: 2 }} />
              </Box>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                These pages connect to the live PostgreSQL database (102.223.7.205:5432):
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <LiveDataIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Live Dashboard" 
                    secondary="Real-time stats from 1,547 schools"
                  />
                  <Button 
                    size="small" 
                    variant="contained"
                    onClick={() => navigate('/live-dashboard')}
                  >
                    Visit
                  </Button>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LiveDataIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Live Analytics" 
                    secondary="Analytics from 828K+ students"
                  />
                  <Button 
                    size="small" 
                    variant="contained"
                    onClick={() => navigate('/live-analytics')}
                  >
                    Visit
                  </Button>
                </ListItem>
              </List>

              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  <strong>Clean APIs:</strong> These use proper endpoints without 
                  parameter leakage and connect to the actual SIS database.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* API Configuration Status */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <DatabaseIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                API Configuration Analysis
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ErrorIcon color="error" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2">
                        Problematic URL Structure
                      </Typography>
                    </Box>
                    <Typography variant="caption" display="block">
                      <code>/analytics/schools/?queryKey%5B%5D=schoolAnalytics</code>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      React Query internal parameters are being sent to backend
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <WarningIcon color="warning" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2">
                        Backend Configuration Needed
                      </Typography>
                    </Box>
                    <Typography variant="body2">
                      Django backend needs to be configured to serve live SIS data 
                      instead of mock data through existing endpoints
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckIcon color="success" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2">
                        Solution Implemented
                      </Typography>
                    </Box>
                    <Typography variant="body2">
                      New live data components with clean API calls and proper 
                      parameter handling
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Recommendations */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🎯 Recommendations for Real-time Data
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Use Live Dashboard & Live Analytics Pages"
                    secondary="Navigate to /live-dashboard and /live-analytics for real SIS database integration"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Backend Configuration Required"
                    secondary="Configure Django backend to serve live data through /analytics/ endpoints or implement new /sis-integration/ endpoints"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <RefreshIcon color="info" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Database Connection Verification"
                    secondary="Ensure PostgreSQL connection to 102.223.7.205:5432 is properly configured in Django settings"
                  />
                </ListItem>
              </List>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={() => navigate('/live-dashboard')}
                >
                  View Live Dashboard
                </Button>
                <Button 
                  variant="contained" 
                  color="secondary"
                  onClick={() => navigate('/live-analytics')}
                >
                  View Live Analytics
                </Button>
                <Button 
                  variant="outlined"
                  onClick={() => navigate('/sis-integration')}
                >
                  SIS Integration Settings
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DataStatus;